import { DateCell, Row, TitleCell } from "@zapier/shared";
import {
  Connection,
  ConnectionContext,
  useAccount,
  useConnectionActionPermissions,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { Tooltip, TooltipFocusableTrigger } from "@zapier/design-system";
import { Icon, type IconName } from "@zapier/zinnia-icons";
import {
  AssetTypes,
  getAppDetailsUrl,
  getServiceIconUrl,
  getZapsHrefForConnection,
  stripVersionFromImplementationName,
  UNTITLED_CONNECTION_DEFAULT,
  UNTITLED_CONNECTION_TITLE,
} from "@zapier/utils";
import { useConnectionMenuItems } from "./useConnectionMenuItems";
import { ConnectionAppCell } from "./cells/ConnectionAppCell";
import { ZapCountCell } from "./cells/ZapCountCell";
import { AccessCell } from "./cells/AccessCell";
import { StatusCell } from "./cells/StatusCell";
import { useTestConnection } from "./useTestConnection";
import { ConnectionModal } from "../types";
import { useReconnectConnection } from "../useReconnectConnection";
import { useBulkTestContext } from "../BulkTestContext";

function AccessIndicator({
  iconName,
  tooltipText,
}: {
  iconName: IconName;
  tooltipText: string;
}) {
  return (
    <Tooltip content={tooltipText} placement="bottom">
      <TooltipFocusableTrigger aria-label={tooltipText}>
        <Icon aria-hidden name={iconName} size={16} color="GrayWarm6" />
      </TooltipFocusableTrigger>
    </Tooltip>
  );
}

interface Props {
  connection: Connection;
  allowSelection?: boolean;
  isAppNonCompliant?: (appId: string) => boolean;
  isNonCompliantFilter?: boolean;
  setCurrentModal: (modal: ConnectionModal | undefined) => void;
  setCurrentConnection: (connection: Connection | undefined) => void;
  connectionIconOverride?: string; // override icon for the connection
  isDisplayOnly?: boolean; // whether to display actions for the row
  showFavoriteButton?: boolean;
}

const ConnectionRow = ({
  allowSelection,
  connection,
  isAppNonCompliant,
  isNonCompliantFilter,
  setCurrentConnection,
  setCurrentModal,
  connectionIconOverride,
  isDisplayOnly = false,
  showFavoriteButton = true,
}: Props) => {
  const t = useTranslations("connections-table");
  const { account } = useAccount();
  const {
    handleTestConnection,
    isTestConnectionError,
    isTestConnectionLoading,
  } = useTestConnection({
    id: connection?.id,
    title: connection?.title ?? "",
  });

  const { canShareConnection } = useConnectionActionPermissions({
    connection,
  });

  const bulkTestStatus = useBulkTestContext()?.getStatus(connection.id);
  const isTestLoading = isTestConnectionLoading || bulkTestStatus === "pending";
  const isTestError = isTestConnectionError || bulkTestStatus === "error";

  const {
    reconnect,
    isAppUnavailable,
    isLoading: isReconnectLoading,
  } = useReconnectConnection(connection);

  const connectionMenuItems = useConnectionMenuItems({
    connection,
    handleTestConnection,
    setCurrentConnection,
    setCurrentModal,
    isTestError,
  });

  const versionlessSelectedApi = connection?.selectedApi?.split("@")[0] ?? "";
  const appVersion = connection.selectedApi?.split("@")[1] ?? "";

  // use icons from implementation object, else grab from generator URL
  const serviceIconSource =
    connectionIconOverride ??
    connection?.implementation?.images?.url64X64 ??
    getServiceIconUrl(versionlessSelectedApi);

  const title =
    connection.title === UNTITLED_CONNECTION_TITLE
      ? UNTITLED_CONNECTION_DEFAULT
      : connection.title;
  const subtitle = connection.label;

  const implementationName = stripVersionFromImplementationName(
    connection?.implementation?.name ?? "",
  );
  const appName = implementationName || versionlessSelectedApi;
  const appHref = connection?.implementation?.slug
    ? getAppDetailsUrl(connection.implementation.slug)
    : undefined;
  const appBadges = connection?.implementation?.badges;

  const zapsHref = getZapsHrefForConnection(connection?.id);

  // A managed connection renders the owning workspace/org as its owner avatar.
  const workspace =
    connection.isManaged && account?.name
      ? { name: account.name, logoUrl: account.logoUrl }
      : undefined;

  // When we show that workspace avatar, drop the synthetic "everyone in the
  // account" group (id "all") — it's the same org logo/name, so it would
  // otherwise render as a duplicate beside the owner avatar.
  const groups = (connection.groups || []).filter(
    (group) => !(workspace && group.id === "all"),
  );

  const sharedWithEveryone =
    (connection.groups || []).find((group) => group.id === "all") || null;

  // isAppNonCompliant checks the frontend guard config; isNonCompliantFilter is a
  // fallback when the backend filter confirms non-compliance but guard data
  // hasn't loaded or doesn't yet reflect the backend's enforcement state.
  const isNonCompliant =
    (isAppNonCompliant?.(connection.selectedApi ?? "") === true ||
      !!isNonCompliantFilter) &&
    !connection.isManaged;

  return (
    <Row
      allowsSelection={allowSelection}
      assetType={AssetTypes.Connection}
      id={String(connection.id)}
      kebabMenuLabel="Connection actions"
      menuItems={connectionMenuItems}
      showFavoriteButton={showFavoriteButton}
    >
      <TitleCell
        assetType={AssetTypes.Connection}
        badge={
          connection.isManaged ? (
            <AccessIndicator
              iconName="miscSecurity"
              tooltipText={t("tooltips.managed-connection")}
            />
          ) : connection.isShared ? (
            <AccessIndicator
              iconName="personGroup"
              tooltipText={t("tooltips.shared-connection")}
            />
          ) : undefined
        }
        id={connection.id}
        isUntitled={connection.title === UNTITLED_CONNECTION_TITLE}
        serviceIconSrc={serviceIconSource}
        subtitle={subtitle}
        title={title}
      />
      <ConnectionAppCell
        name={appName}
        version={appVersion}
        href={appHref}
        badges={appBadges}
      />
      <StatusCell
        appName={appName}
        connection={connection}
        isError={isTestError}
        isLoading={isTestLoading}
        isNonCompliant={isNonCompliant}
        isRestricted={!!connection.isRestricted}
        isStale={!!connection.isStale}
        isDisplayOnly={isDisplayOnly}
        isAppUnavailable={isAppUnavailable}
        isReconnectLoading={isReconnectLoading}
        onReconnect={reconnect}
        onReplace={() => {
          setCurrentConnection(connection);
          setCurrentModal(ConnectionModal.Replace);
        }}
        onTest={handleTestConnection}
      />
      <ZapCountCell count={connection.zapCount || 0} href={zapsHref} />
      <DateCell datetime={connection.lastchanged || new Date()} />
      <AccessCell
        groups={groups}
        members={connection.members || []}
        owner={connection.owner}
        sharedWithEveryoneGroup={sharedWithEveryone}
        isShareDisabled={!canShareConnection || !!connection.isRestricted}
        onShare={() => {
          setCurrentConnection(connection);
          setCurrentModal(ConnectionModal.Share);
        }}
      />
    </Row>
  );
};

/**
 * This needs to be in a separate component so that the useConnectionActionPermissions
 * hook has access to the provider context when it is called.
 */
const ConnectionRowContainer = (props: Props) => {
  return (
    <ConnectionContext.Provider value={props.connection}>
      <ConnectionRow {...props} />
    </ConnectionContext.Provider>
  );
};

export default ConnectionRowContainer;
